import React, { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { ActionSheet, Button, Dialog, Space, SpinLoading, Toast } from 'antd-mobile';
import type { Action } from 'antd-mobile/es/components/action-sheet';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ApiEnvelope } from '../api/types';
import { campaignApi } from '../api/campaigns';
import { whatsappApi, WhatsAppConnectionState, WhatsAppDisconnectStrategy, MyWhatsAppAccountBinding } from '../api/whatsapp';
import { normalizeMediaUrl } from '../utils/media';

interface ForwardResponsePayload {
  allocated_recipient_number?: string;
  target_numbers?: string[];
  status?: 'queued' | 'failed';
}

interface ApiFailureResponse {
  error?: {
    message?: string;
  } | string;
}

interface ForwardSheetProps {
  open: boolean;
  content: string;
  mediaUrl?: string;
  campaignId?: string;
  promotionLimit?: number;
  preferredBindingId?: string;
  allowAccountSwitch?: boolean;
  hideContextCards?: boolean;
  onClose: () => void;
  onSubmitted?: () => void;
}

const FORWARD_DISCONNECT_STRATEGY: WhatsAppDisconnectStrategy = 'manual';

export const ForwardSheet: React.FC<ForwardSheetProps> = ({
  open,
  content,
  mediaUrl,
  campaignId,
  promotionLimit = 1,
  preferredBindingId,
  allowAccountSwitch = true,
  onClose,
  onSubmitted,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation('forward');
  const [accountBindings, setAccountBindings] = useState<MyWhatsAppAccountBinding[]>([]);
  const [selectedBindingId, setSelectedBindingId] = useState<string | null>(null);
  const [accountPickerVisible, setAccountPickerVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectionStateLoading, setConnectionStateLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [connectionState, setConnectionState] = useState<WhatsAppConnectionState | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const selectedBinding = useMemo(() => (
    accountBindings.find((binding) => binding.id === selectedBindingId)
    || accountBindings.find((binding) => binding.is_default)
    || accountBindings[0]
    || null
  ), [accountBindings, selectedBindingId]);

  const connectionStatus = connectionState?.status || 'not_initialized';
  const isConnectionReady = connectionStatus === 'ready' || connectionStatus === 'connected';
  const isBindingVerified = Boolean(selectedBinding?.verified_at);
  const canSubmit = Boolean(campaignId) && Boolean(selectedBinding) && isBindingVerified && isConnectionReady && !submitting;
  const isConnectionBusy = loading || connectionStateLoading;
  const canSwitchAccount = allowAccountSwitch && accountBindings.length > 1;
  const selectedAccountLabel = selectedBinding
    ? `${selectedBinding.account.phone_number}${selectedBinding.alias ? ` · ${selectedBinding.alias}` : ''}`
    : t('confirm.noAccount');
  const contentPreview = useMemo(() => content.replace(/\s+/g, ' ').trim(), [content]);

  const accountStatusHint = (() => {
    if (!selectedBinding) {
      return errorMessage || t('missingBoundError');
    }
    if (!isBindingVerified) {
      return t('gating.unverifiedHint');
    }
    if (errorMessage) {
      return errorMessage;
    }
    if (isConnectionReady) {
      return t('confirm.readyHint');
    }
    if (connectionState?.message) {
      return connectionState.message;
    }
    return t('confirm.notReadyHint');
  })();

  const showGoConnectButton = !selectedBinding || !isBindingVerified || !isConnectionReady;
  const submitButtonText = submitting ? t('submitting') : t('confirm.submit');
  const disconnectSuccessMessage = t('disconnectKeep10mMessage');

  const loadConnectionState = async (waAccountId?: string | null): Promise<void> => {
    if (!waAccountId) {
      setConnectionState(null);
      setErrorMessage('');
      return;
    }

    setConnectionStateLoading(true);
    try {
      const response = await whatsappApi.getConnectionState(waAccountId);
      setConnectionState(response.data.data as WhatsAppConnectionState);
      setErrorMessage('');
    } catch (error) {
      const message = extractApiErrorMessage(error, t('fetchStateFailed'));
      setConnectionState(null);
      setErrorMessage(message);
    } finally {
      setConnectionStateLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      setAccountBindings([]);
      setSelectedBindingId(null);
      setAccountPickerVisible(false);
      setLoading(false);
      setConnectionStateLoading(false);
      setSubmitting(false);
      setConnectionState(null);
      setErrorMessage('');
      return;
    }

    const initialize = async () => {
      setLoading(true);
      setErrorMessage('');

      try {
        const bindingsResponse = await whatsappApi.getMyAccounts() as { data: ApiEnvelope<MyWhatsAppAccountBinding[]> };
        const bindings = bindingsResponse.data.data || [];
        const defaultBinding = (
          (preferredBindingId ? bindings.find((binding) => binding.id === preferredBindingId) : null)
          || bindings.find((binding) => binding.is_default)
          || bindings[0]
          || null
        );

        setAccountBindings(bindings);
        setSelectedBindingId(defaultBinding?.id || null);
        await loadConnectionState(defaultBinding?.wa_account_id || null);
      } catch (error) {
        setAccountBindings([]);
        setSelectedBindingId(null);
        setConnectionState(null);
        const message = extractApiErrorMessage(error, t('missingBoundError'));
        setErrorMessage(message);
        Toast.show({
          icon: 'fail',
          content: message,
        });
      } finally {
        setLoading(false);
      }
    };

    void initialize();
  }, [open, preferredBindingId, t]);

  const handleSubmit = async () => {
    if (!campaignId) {
      Toast.show({ icon: 'fail', content: t('missingCampaignToast') });
      return;
    }

    if (!selectedBinding) {
      Toast.show({ icon: 'fail', content: t('missingBoundError') });
      return;
    }

    if (!isBindingVerified) {
      Toast.show({ icon: 'fail', content: t('gating.unverifiedToast') });
      return;
    }

    if (!isConnectionReady) {
      Toast.show({ icon: 'fail', content: t('needConnectToast') });
      return;
    }

    setSubmitting(true);
    try {
      const response = await campaignApi.createSendBatch(campaignId, {
        content,
        media_url: mediaUrl,
        wa_account_id: selectedBinding.wa_account_id,
      });
      const forwardPayload = response.data?.data as ForwardResponsePayload | undefined;
      const allocatedRecipientNumber = forwardPayload?.allocated_recipient_number;
      const batchCount = forwardPayload?.target_numbers?.length || 0;
      Toast.show({
        content: batchCount > 0
          ? t('batchSubmittedToast', { count: batchCount })
          : allocatedRecipientNumber
          ? t('submittedWithRecipient', { recipient: allocatedRecipientNumber, disconnectMessage: disconnectSuccessMessage })
          : t('submittedToast', { disconnectMessage: disconnectSuccessMessage }),
      });
      onSubmitted?.();
      onClose();
    } catch (error) {
      Toast.show({
        icon: 'fail',
        content: extractApiErrorMessage(error, t('forwardFailed')),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSelectBinding = async (binding: MyWhatsAppAccountBinding): Promise<void> => {
    if (binding.id === (selectedBinding?.id || '')) {
      return;
    }

    setSelectedBindingId(binding.id);
    setErrorMessage('');
    await loadConnectionState(binding.wa_account_id);
  };

  const accountActions: Action[] = accountBindings.map((binding) => ({
    key: binding.id,
    text: (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span style={{ width: 16, display: 'inline-block', textAlign: 'center' }}>
          {binding.id === (selectedBinding?.id || '') ? '✓' : ''}
        </span>
        <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontWeight: 700 }}>{binding.account.phone_number}</span>
          <span style={{ fontSize: 12, color: '#7f879b' }}>
            {binding.alias || (binding.verified_at ? t('gating.verifiedTag') : t('gating.unverifiedTag'))}
          </span>
        </span>
      </span>
    ),
    bold: binding.id === (selectedBinding?.id || ''),
    onClick: () => {
      setAccountPickerVisible(false);
      void handleSelectBinding(binding);
    },
  }));

  const handleGoConnect = () => {
    const returnTo = encodeURIComponent(window.location.pathname + window.location.search);
    const connectQuery = selectedBinding?.id
      ? `?connect=${encodeURIComponent(selectedBinding.id)}&returnTo=${returnTo}`
      : `?returnTo=${returnTo}`;
    navigate(`/profile/whatsapp-accounts${connectQuery}`);
    onClose();
  };

  return (
    <Dialog
      visible={open}
      title={t('confirm.title')}
      bodyStyle={{
        maxHeight: 'calc(100vh - 32px)',
      }}
      actions={[
        [
          {
            key: 'cancel',
            text: t('cancel'),
            onClick: onClose,
            style: {
              color: '#4e5969',
              background: '#f7f8fa',
              borderRight: '1px solid rgba(229, 234, 243, 0.9)',
              fontWeight: 600,
              letterSpacing: 0.2,
              opacity: 1,
            } as React.CSSProperties,
          },
          {
            key: 'submit',
            text: submitButtonText,
            bold: true,
            disabled: !canSubmit,
            onClick: canSubmit ? handleSubmit : undefined,
            style: {
              color: canSubmit ? '#ffffff' : '#8aa8df',
              background: canSubmit ? 'linear-gradient(135deg, #4d8dff 0%, #1769ff 100%)' : '#e8f0ff',
              fontWeight: 700,
              letterSpacing: 0.2,
              opacity: 1,
            } as React.CSSProperties,
          },
        ],
      ]}
      content={
        <>
          <ActionSheet
            visible={accountPickerVisible}
            actions={accountActions}
            cancelText={t('switchAccountCancel')}
            onClose={() => setAccountPickerVisible(false)}
          />
          <div style={{ width: '100%' }}>
            <Space direction="vertical" block style={{ width: '100%' }}>
              <div style={{ fontSize: 12, color: '#98a2b3', lineHeight: 1.4 }}>
                {t('confirm.subtitle')}
              </div>

              <div
                style={{
                  borderRadius: 16,
                  padding: '12px 14px',
                  border: '1px solid rgba(32, 36, 55, 0.06)',
                  background: '#ffffff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  gap: 10,
                }}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#7f879b', letterSpacing: 0.2 }}>
                    {t('accountLabel')}
                  </div>
                  <div style={{ marginTop: 4, fontSize: 14, fontWeight: 800, color: '#202437', wordBreak: 'break-all' }}>
                    {selectedAccountLabel}
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {selectedBinding ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          padding: '2px 8px',
                          borderRadius: 999,
                          background: isBindingVerified ? 'rgba(18, 161, 80, 0.10)' : 'rgba(212, 107, 8, 0.10)',
                          color: isBindingVerified ? '#12a150' : '#d46b08',
                          fontSize: 11,
                          fontWeight: 700,
                        }}
                      >
                        {isBindingVerified ? t('gating.verifiedTag') : t('gating.unverifiedTag')}
                      </span>
                    ) : null}
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: isConnectionReady ? 'rgba(23, 105, 255, 0.10)' : 'rgba(143, 149, 163, 0.12)',
                        color: isConnectionReady ? '#1769ff' : '#7f879b',
                        fontSize: 11,
                        fontWeight: 700,
                      }}
                    >
                      {isConnectionReady ? t('confirm.connectedTag') : t('confirm.notConnectedTag')}
                    </span>
                  </div>
                </div>
                {canSwitchAccount ? (
                  <Button
                    size="small"
                    fill="outline"
                    disabled={isConnectionBusy}
                    style={{ borderRadius: 999, fontWeight: 600 }}
                    onClick={() => setAccountPickerVisible(true)}
                  >
                    {t('switchAccount')}
                  </Button>
                ) : null}
              </div>

              <div
                style={{
                  borderRadius: 16,
                  padding: '12px 14px',
                  border: '1px solid rgba(32, 36, 55, 0.06)',
                  background: '#ffffff',
                }}
              >
                <div style={{ fontSize: 11, fontWeight: 700, color: '#7f879b', letterSpacing: 0.2 }}>
                  {t('confirm.contentTitle')}
                </div>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    marginTop: 10,
                  }}
                >
                  {mediaUrl ? (
                    <img
                      src={normalizeMediaUrl(mediaUrl)}
                      alt={t('confirm.mediaAlt')}
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: 12,
                        objectFit: 'cover',
                        flexShrink: 0,
                        boxShadow: '0 6px 18px rgba(31, 35, 41, 0.08)',
                      }}
                    />
                  ) : null}
                  <div
                    style={{
                      minWidth: 0,
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: 12,
                      background: '#f7f8fa',
                      border: '1px solid rgba(32, 36, 55, 0.05)',
                    }}
                  >
                    <div
                      style={{
                        color: '#1f2329',
                        fontSize: 13,
                        lineHeight: 1.7,
                        wordBreak: 'break-word',
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        overflow: 'hidden',
                      }}
                    >
                      {contentPreview || content}
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  borderRadius: 16,
                  padding: '10px 12px',
                  border: '1px solid rgba(23, 105, 255, 0.08)',
                  background: 'rgba(23, 105, 255, 0.04)',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#4e5969', lineHeight: 1.5 }}>
                      {accountStatusHint}
                    </div>
                    <div style={{ marginTop: 4, fontSize: 11, color: '#98a2b3', lineHeight: 1.5 }}>
                      {t('confirm.tipDetail', { value: promotionLimit })}
                    </div>
                  </div>
                  {isConnectionBusy ? <SpinLoading style={{ '--size': '22px' }} /> : null}
                </div>
                {showGoConnectButton ? (
                  <Button
                    block
                    style={{
                      marginTop: 10,
                      height: 38,
                      borderRadius: 999,
                      background: '#f4f7ff',
                      border: '1px solid rgba(23, 105, 255, 0.1)',
                      color: '#1769ff',
                      fontWeight: 700,
                    }}
                    onClick={handleGoConnect}
                  >
                    {t('gating.goConnect')}
                  </Button>
                ) : null}
              </div>
            </Space>
          </div>
        </>
      }
      onClose={onClose}
    />
  );
};

function extractApiErrorMessage(error: unknown, fallbackMessage: string): string {
  const axiosError = error as AxiosError<ApiFailureResponse>;
  const responseError = axiosError.response?.data?.error;
  if (typeof responseError === 'string' && responseError.trim()) {
    return responseError;
  }

  if (responseError && typeof responseError === 'object' && responseError.message) {
    return responseError.message;
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return fallbackMessage;
}
